(ns manage.adset-decision-tree-page.view.side-panel.selection-detail
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/AlertTitle$default" :as AlertTitle]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/Stack$default" :as Stack]
            [clojure.string :as str]
            [common.flags :refer [use-flags]]
            [manage.adset-decision-tree-page.selection :as selection]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.predicate-editor.core
             :refer [predicate-editor]]
            [manage.adset-decision-tree-page.view.side-panel.ad-variants
             :refer [ad-variant-section]]
            [manage.adset-decision-tree-page.view.side-panel.editors
             :refer [ad-variant-pixel-editor autocomplete-editor lat-lon-editor meta-adset-editor text-input tiktok-ad-groups-editor]]
            [manage.adset-decision-tree-page.view.side-panel.feed.core
             :refer [feed-outcome-source feed-properties]]
            [manage.adset-decision-tree-page.view.side-panel.node-outcomes
             :refer [node-outcomes-editor]]
            [manage.adset-decision-tree-page.view.side-panel.poi.core
             :refer [poi-properties]]
            [manage.adset-decision-tree-page.view.side-panel.poi.predicate-editor
             :refer [poi-predicate-editor]]
            [manage.adset-decision-tree-page.view.side-panel.product-selector-editor
             :refer [product-selector-editor]]
            [manage.adset-decision-tree-page.view.side-panel.property-form
             :refer [use-property-editor-props use-props-with-property-field-array]]
            [manage.adset-decision-tree-page.view.side-panel.tagging :as tagging]
            [manage.adset-decision-tree-page.view.side-panel.trackers :as trackers]
            [manage.adset-integration-meta-settings.db :as meta-settings-db]
            [manage.adset.hooks :as adset.h]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui properties-info [{{:keys [title message]} :property}]
  ($ Alert {:severity "info"}
     (when title ($ AlertTitle title))
     message))

(defui meta-platform-settings-warning [_props]
  (let [integration-settings (meta-settings-db/use-integration-settings)
        missing-account? (str/blank? (:facebook-ad-account-id integration-settings))]
    (when missing-account?
      ($ Alert {:severity "warning"
                :action ($ Button {:size "small"
                                   :color "inherit"
                                   :onClick #(rf/dispatch
                                              [:modal/open
                                               {:id :adset-integration-settings/modal-edit}])}
                           "Settings")}
         ($ AlertTitle "Meta AD Account ID missing")
         "Please provide your Meta AD Account ID to enable Meta adset selection."))))

(defui node-warning [{:keys [warnings]}]
  ($ :<>
     (for [{:keys [message]} warnings]
       (let [parts (str/split message #"\{\{|\}\}")
             has-placeholder (> (count parts) 1)]
         ($ Alert {:key message
                   :severity "warning"}
            (if has-placeholder
              ($ :<>
                 (for [[idx part] (map-indexed vector parts)]
                   (if (odd? idx)
                     ($ :strong {:key idx} part)
                     ($ :span {:key idx} part))))
              message))))))

(defui property-editor [{:keys [property selected-node] :as props}]
  (let [editor (or (:editor property)
                   (case (:type property)
                     :string text-input
                     :autocomplete autocomplete-editor
                     :meta.facebook/adset-id-autocomplete meta-adset-editor
                     :tiktok/ad-group-autocomplete tiktok-ad-groups-editor
                     :ad-variant/tagging tagging/ad-variant-tagging-editor
                     :ad-variant/trackers trackers/ad-variant-trackers-editor
                     :lat-lon lat-lon-editor
                     :targeting/predicate predicate-editor
                     :targeting/poi-predicate poi-predicate-editor
                     :properties/info properties-info
                     :meta.facebook/platform-settings-warning meta-platform-settings-warning
                     :ad-variant/pixel ad-variant-pixel-editor
                     :feed/properties feed-properties
                     :feed/outcome-source feed-outcome-source
                     :poi/properties poi-properties
                     nil))]
    (if (nil? editor)
      ($ :div "Missing editor: " (str (:type property)))
      ($ editor props))))

(defui field-array-property-editor [props]
  ($ property-editor (use-props-with-property-field-array props)))

(defui property-form [props]
  (let [property-editor-props (use-property-editor-props props)]
    ($ Stack {:spacing 1
              :sx #js {:px 1}}
       (for [{{:keys [use-field-array]} :property :as editor-props} property-editor-props
             :let [editor-component (if use-field-array
                                      field-array-property-editor
                                      property-editor)]]
         ($ editor-component editor-props)))))

(defhook use-selection-details []
  (let [selected-item (use-subscribe [::sub/first-selected-item])
        current-channel (adset.h/use-channel)
        selected-node (use-subscribe [::sub/selected-node selected-item])
        flag-values (use-flags selection/feature-flags)]
    (uix/use-memo
     #(merge
       {:selected-item selected-item
        :selected-node selected-node}
       (selection/get-selection-details selected-item selected-node
                                        current-channel flag-values))
     [selected-item current-channel selected-node flag-values])))

(defui root-view []
  (let [{:keys [properties has-variants has-outcomes selected-item selected-node]
         :as selection-details} (use-selection-details)

        selected-item-type (:type selected-item)]
    ($ Stack {:spacing 2
              :divider ($ Divider)
              :p 1
              :mt 1}

       (when (:warnings selected-node)
         ($ node-warning selected-node))

       ;; Special handling for product selector selection type
       (when (= selected-item-type :selection/product-selector)
         ($ product-selector-editor selection-details))

       (when (seq properties)
         ($ property-form (assoc selection-details :key selected-item)))

       (when has-variants
         ($ ad-variant-section selection-details))
       (when has-outcomes
         ($ node-outcomes-editor selection-details)))))
